'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BodyPortal } from '@/components/ui/BodyPortal'
import { useUser } from '@/contexts/UserContext'
import { useChatRealtime } from '@/contexts/ChatRealtimeContext'
import type { ChatAttachment } from '@/contexts/ChatRealtimeContext'
import { uploadSupportAttachment } from '@/app/support/_components/support-utils'
import { pushBackHandler } from '@/lib/mobileBackStack'

function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth <= 768
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
const SELECTED_USER_STORAGE_KEY = 'chat_drawer_selected_user_id'

type ChatContact = {
  id: number
  name: string
  email: string
  firm_id: number | null
  firm_name?: string
  last_seen: number
  last_message_at: number
  last_message_text: string
  unread_count: number
  online: boolean
}

type ChatMessage = {
  id: number
  created_at: number
  from_user_id: number
  to_user_id: number
  message: string
  attachments?: ChatAttachment[]
  read_at: number
}

function fileUrl(path?: string) {
  const raw = String(path || '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return raw
  return `${API_BASE.replace(/\/api\/?$/, '')}${raw.startsWith('/') ? raw : `/${raw}`}`
}

function getInitials(name: string) {
  const n = String(name || '').trim()
  if (!n) return '?'
  const parts = n.split(/\s+/).filter(Boolean)
  const a = (parts[0] || '').slice(0, 1)
  const b = (parts[1] || '').slice(0, 1)
  return (a + b).toUpperCase() || a.toUpperCase() || '?'
}

function avatarColor(seed: string) {
  const s = String(seed || '').trim().toLowerCase()
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  const hue = h % 360
  return {
    bg: `hsl(${hue} 62% 32%)`,
    fg: '#fff'
  }
}

function formatTime(ts: number) {
  const n = typeof ts === 'number' ? ts : parseInt(ts as any, 10)
  if (!n || !Number.isFinite(n)) return ''
  try {
    const d = new Date(n)
    if (!Number.isFinite(d.getTime())) return ''
    const s = d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
    // Some runtimes return literal "Invalid Date" instead of throwing.
    if (!s || s === 'Invalid Date') return ''
    return s
  } catch {
    return ''
  }
}

function getStoredSelectedUserId() {
  if (typeof window === 'undefined') return null
  const n = parseInt(localStorage.getItem(SELECTED_USER_STORAGE_KEY) || '', 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function ChatDrawer() {
  const { user } = useUser()
  const {
    onlineUserIdSet,
    onlineVersion,
    lastMessage,
    lastMessageSeq,
    setActiveChatUserId,
    markRead,
    sendMessage: sendMessageRealtime,
    syncUnreadTotal,
    drawerOpen,
    chatPinned,
    closeDrawer,
    setChatPinned,
    requestedOpenUserId,
    requestedOpenUserSeq,
    notifySettings,
    setNotifySettings,
  } = useChatRealtime()

  const [contacts, setContacts] = useState<ChatContact[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  const [contactSearch, setContactSearch] = useState('')
  const [onlineOnly, setOnlineOnly] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const pinned = chatPinned

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [sending, setSending] = useState(false)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [previewImage, setPreviewImage] = useState<{ src: string; name: string } | null>(null)

  const bottomRef = useRef<HTMLDivElement | null>(null)
  const messagesWrapRef = useRef<HTMLDivElement | null>(null)
  const stickToBottomRef = useRef(true)
  const composerInputRef = useRef<HTMLInputElement | null>(null)
  const drawerRef = useRef<HTMLElement | null>(null)
  const backCleanupRef = useRef<(() => void) | null>(null)
  const chatBackCleanupRef = useRef<(() => void) | null>(null)
  const drawerWasOpenRef = useRef(false)

  // Defensive: selected id may become a string if assigned from raw API data.
  // Keep a normalized numeric version for all comparisons.
  const selectedUserIdNum = useMemo(() => {
    const v: any = selectedUserId as any
    const n = typeof v === 'number' ? v : parseInt(v, 10)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [selectedUserId])

  const token = useMemo(() => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('auth_token')
  }, [drawerOpen])

  const togglePinned = useCallback(() => {
    setChatPinned(!pinned)
  }, [pinned, setChatPinned])

  const closeChatDrawer = useCallback(() => {
    if (pinned) {
      setChatPinned(false)
    }
    closeDrawer()
  }, [closeDrawer, pinned, setChatPinned])

  const focusComposer = useCallback((delay = 0) => {
    if (delay > 0) {
      window.setTimeout(() => composerInputRef.current?.focus(), delay)
      return
    }
    requestAnimationFrame(() => composerInputRef.current?.focus())
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const cls = 'chat-drawer-pinned-open'
    document.body.classList.toggle(cls, drawerOpen && pinned)
    return () => {
      document.body.classList.remove(cls)
    }
  }, [drawerOpen, pinned])

  const fetchContacts = useCallback(async () => {
    if (!token) return
    try {
      setContactsLoading(true)
      const params = new URLSearchParams()
      params.set('limit', '500')
      if (contactSearch.trim()) params.set('search', contactSearch.trim())
      if (onlineOnly) params.set('online_only', '1')

      const res = await fetch(`${API_BASE}/chat/contacts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.success) {
        setContacts([])
        return
      }
      const raw = Array.isArray(data.data) ? data.data : []
      const normalized: ChatContact[] = raw
        .filter(Boolean)
        .map((c: any) => ({
          id: parseInt(c.id, 10),
          name: String(c.name || ''),
          email: String(c.email || ''),
          firm_id: c.firm_id == null ? null : parseInt(c.firm_id, 10),
          firm_name: c.firm_name ? String(c.firm_name) : undefined,
          last_seen: parseInt(c.last_seen, 10) || 0,
          last_message_at: parseInt(c.last_message_at, 10) || 0,
          last_message_text: String(c.last_message_text || ''),
          unread_count: parseInt(c.unread_count, 10) || 0,
          online: !!c.online,
        }))
        .filter((c: ChatContact) => !!c.id)
        .sort((a: ChatContact, b: ChatContact) => {
          const ta = a.last_message_at || 0
          const tb = b.last_message_at || 0
          if (tb !== ta) return tb - ta
          return (b.last_seen || 0) - (a.last_seen || 0)
        })

      setContacts(normalized)
    } finally {
      setContactsLoading(false)
    }
  }, [contactSearch, onlineOnly, token])

  const fetchMessages = useCallback(
    async (otherUserId: number) => {
      if (!token) return
      try {
        setMessagesLoading(true)
        const res = await fetch(`${API_BASE}/chat/messages/${otherUserId}?limit=80`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data?.success) {
          setMessages([])
          return
        }
        const raw = Array.isArray(data.data) ? data.data : []
        const normalized: ChatMessage[] = raw
          .filter(Boolean)
          .map((m: any) => ({
            id: parseInt(m.id, 10),
            created_at: parseInt(m.created_at, 10) || 0,
            from_user_id: parseInt(m.from_user_id, 10) || 0,
            to_user_id: parseInt(m.to_user_id, 10) || 0,
            message: String(m.message || ''),
            attachments: Array.isArray(m.attachments) ? m.attachments : [],
            read_at: parseInt(m.read_at, 10) || 0,
          }))
          .filter((m: ChatMessage) => !!m.id)
        setMessages(normalized)

        // mark read (socket-first; REST fallback handled by provider)
        try {
          await markRead(otherUserId)
          // keep global unread badge consistent
          syncUnreadTotal().catch(() => {})
        } catch {
          // ignore
        }

        setContacts((prev) => prev.map((c) => (c.id === otherUserId ? { ...c, unread_count: 0 } : c)))
      } finally {
        setMessagesLoading(false)
      }
    },
    [markRead, syncUnreadTotal, token]
  )

  useEffect(() => {
    if (!drawerOpen) {
      drawerWasOpenRef.current = false
      return
    }
    if (drawerWasOpenRef.current) return
    drawerWasOpenRef.current = true
    if (!token) return

    // Initial load (REST)
    fetchContacts()
    setSettingsOpen(false)

    // Open state: start empty until user selects a person.
    setSelectedUserId(pinned ? getStoredSelectedUserId() : null)
    setMessages([])
    setMessageText('')
    stickToBottomRef.current = true

    requestAnimationFrame(() => composerInputRef.current?.focus())

    return () => {
      setActiveChatUserId(null)
    }
  }, [drawerOpen, fetchContacts, pinned, setActiveChatUserId, token])

  // If a toast requested opening a specific chat, select it.
  useEffect(() => {
    if (!drawerOpen) return
    if (!requestedOpenUserId) return
    stickToBottomRef.current = true
    setSelectedUserId(requestedOpenUserId)
    try {
      localStorage.setItem(SELECTED_USER_STORAGE_KEY, String(requestedOpenUserId))
    } catch {
      // ignore
    }
    focusComposer(120)
    syncUnreadTotal().catch(() => {})
  }, [drawerOpen, focusComposer, requestedOpenUserId, requestedOpenUserSeq, syncUnreadTotal])

  useEffect(() => {
    if (!selectedUserIdNum) return
    try {
      localStorage.setItem(SELECTED_USER_STORAGE_KEY, String(selectedUserIdNum))
    } catch {
      // ignore
    }
  }, [selectedUserIdNum])

  // Keep provider's active chat id in sync (used for unread gating).
  useEffect(() => {
    if (!drawerOpen) return
    setActiveChatUserId(selectedUserIdNum)
  }, [drawerOpen, selectedUserIdNum, setActiveChatUserId])

  // Overlay online flags from the provider snapshot.
  useEffect(() => {
    if (!drawerOpen) return
    setContacts((prev) => prev.map((c) => ({ ...c, online: onlineUserIdSet.has(c.id) })))
  }, [drawerOpen, onlineUserIdSet, onlineVersion])

  // Apply incoming messages from provider (single socket listener lives in the provider).
  useEffect(() => {
    if (!drawerOpen) return
    if (!lastMessage || !lastMessage.id) return
    if (!user?.id) return

    const msg = lastMessage
    const otherId = msg.from_user_id === user.id ? msg.to_user_id : msg.from_user_id
    if (!otherId) return

    // Update contact preview ordering and unread counts
    setContacts((prev) => {
      const idx = prev.findIndex((c) => c.id === otherId)
      if (idx === -1) return prev
      const c = prev[idx]
      const isForSelected = selectedUserIdNum === otherId
      const incomingFromOther = msg.to_user_id === user.id && msg.from_user_id === otherId
      const unreadInc = !isForSelected && incomingFromOther ? 1 : 0

      const updated: ChatContact = {
        ...c,
        last_message_at: msg.created_at,
        last_message_text: msg.message || (msg.attachments?.length ? 'Görsel' : ''),
        unread_count: Math.max(0, (c.unread_count || 0) + unreadInc),
      }
      const next = [...prev]
      next.splice(idx, 1)
      next.unshift(updated)
      return next
    })

    // Append to current chat if relevant
    if (selectedUserIdNum && (msg.from_user_id === selectedUserIdNum || msg.to_user_id === selectedUserIdNum)) {
      setMessages((prev) => {
        if (prev.some((x) => x.id === msg.id)) return prev
        stickToBottomRef.current = true
        return [...prev, msg]
      })

      // mark read if incoming from selected
      if (msg.to_user_id === user.id && msg.from_user_id === selectedUserIdNum) {
        markRead(selectedUserIdNum).catch(() => {})
        syncUnreadTotal().catch(() => {})
        setContacts((prev) => prev.map((c) => (c.id === selectedUserIdNum ? { ...c, unread_count: 0 } : c)))
      }
    }
  }, [drawerOpen, lastMessageSeq, lastMessage, markRead, selectedUserIdNum, syncUnreadTotal, user?.id])

  // Refetch contacts when filters/search change while open
  useEffect(() => {
    if (!drawerOpen) return
    fetchContacts()
  }, [contactSearch, drawerOpen, fetchContacts, onlineOnly])

  useEffect(() => {
    if (!drawerOpen) return
    if (!selectedUserIdNum) return

    fetchMessages(selectedUserIdNum)
    focusComposer()
  }, [fetchMessages, focusComposer, drawerOpen, selectedUserIdNum])

  useEffect(() => {
    if (!drawerOpen) return
    if (!selectedUserIdNum) return
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ block: 'end' })
    })
  }, [drawerOpen, messages.length, selectedUserIdNum])

  // ESC closes
  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeChatDrawer()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closeChatDrawer, drawerOpen])

  // ── Mobile: back button handling ──
  useEffect(() => {
    if (!drawerOpen || !isMobile()) return
    const remove = pushBackHandler(() => closeChatDrawer())
    backCleanupRef.current = remove
    return () => {
      remove()
      backCleanupRef.current = null
    }
  }, [drawerOpen, closeChatDrawer])

  // Mobile: push extra back handler when a contact is selected (chat view)
  useEffect(() => {
    if (!isMobile()) return
    if (selectedUserIdNum && drawerOpen) {
      const remove = pushBackHandler(() => {
        setSelectedUserId(null)
        setMessages([])
      })
      chatBackCleanupRef.current = remove
      return () => {
        remove()
        chatBackCleanupRef.current = null
      }
    }
    if (chatBackCleanupRef.current) {
      chatBackCleanupRef.current()
      chatBackCleanupRef.current = null
    }
  }, [selectedUserIdNum, drawerOpen])

  // ── Mobile: visualViewport keyboard handling ──
  useEffect(() => {
    if (!drawerOpen || !isMobile()) return
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      const el = drawerRef.current
      if (!el) return
      const keyboardOpen = vv.height < window.innerHeight * 0.85
      if (keyboardOpen) {
        el.style.height = `${vv.height}px`
        el.style.top = `${vv.offsetTop}px`
      } else {
        el.style.height = ''
        el.style.top = ''
      }
    }
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      const el = drawerRef.current
      if (el) { el.style.height = ''; el.style.top = '' }
    }
  }, [drawerOpen])

  const selectedContact = useMemo(
    () => (selectedUserIdNum ? (contacts.find((c) => c.id === selectedUserIdNum) || null) : null),
    [contacts, selectedUserIdNum]
  )

  const { recentContacts, otherContacts } = useMemo(() => {
    const list = contacts
    const recent = list
      .filter((c) => (c.last_message_at || 0) > 0)
      .sort((a, b) => (b.last_message_at || 0) - (a.last_message_at || 0))

    const recentIds = new Set(recent.map((c) => c.id))
    const other = list
      .filter((c) => !recentIds.has(c.id))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr'))

    return { recentContacts: recent, otherContacts: other }
  }, [contacts])

  const sendMessage = useCallback(async (attachments: ChatAttachment[] = []) => {
    if (!token) return
    if (!selectedUserIdNum) return
    const text = messageText.trim()
    const files = Array.isArray(attachments) ? attachments : []
    if (!text && files.length === 0) return
    if (sending) return

    try {
      setSending(true)

      // optimistic append
      const tempId = -Date.now()
      const now = Date.now()
      setMessages((prev) => [
        ...prev,
        {
          id: tempId,
          created_at: now,
          from_user_id: user?.id || 0,
          to_user_id: selectedUserIdNum,
          message: text,
          attachments: files,
          read_at: now,
        },
      ])

      // Update contacts list locally (avoid refetch spam).
      setContacts((prev) => {
        const idx = prev.findIndex((c) => c.id === selectedUserIdNum)
        if (idx === -1) return prev
        const c = prev[idx]
        const updated: ChatContact = {
          ...c,
          last_message_at: now,
          last_message_text: text || (files.length ? 'Görsel' : ''),
          unread_count: 0,
        }
        const next = [...prev]
        next.splice(idx, 1)
        next.unshift(updated)
        return next
      })

      setMessageText('')
      stickToBottomRef.current = true
      // Keep input responsive; don't wait for network.
      composerInputRef.current?.focus()
      // On polling transport, the server echo can arrive later. Ensure the UI
      // immediately scrolls so the optimistic bubble is visible.
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' })
      })

      const saved = await sendMessageRealtime(selectedUserIdNum, text, files)
      if (!saved) {
        setMessages((prev) => prev.filter((m) => m.id !== tempId))
        return
      }
      setMessages((prev) => {
        // If the same message already arrived via realtime (provider) before this
        // ack resolved, we may already have `saved.id` in the list.
        // In that case, just drop the optimistic temp message to avoid duplicate keys.
        const withoutTemp = prev.filter((m) => m.id !== tempId)
        if (withoutTemp.some((m) => m.id === saved.id)) return withoutTemp
        return [...withoutTemp, saved]
      })
    } finally {
      setSending(false)
      requestAnimationFrame(() => composerInputRef.current?.focus())
    }
  }, [messageText, selectedUserIdNum, sendMessageRealtime, sending, token, user?.id])

  const handleComposerPaste = useCallback(async (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (!selectedUserIdNum || sending || uploadingAttachment) return
    const items = Array.from(event.clipboardData?.items || [])
    const imageItem = items.find((item) => item.kind === 'file' && item.type.startsWith('image/'))
    const file = imageItem?.getAsFile()
    if (!file) return

    event.preventDefault()
    try {
      setUploadingAttachment(true)
      const ext = file.type.split('/')[1] || 'png'
      const namedFile = new File([file], file.name || `sohbet-gorsel-${Date.now()}.${ext}`, { type: file.type || 'image/png' })
      const uploaded = await uploadSupportAttachment(namedFile)
      if (!uploaded.path) return
      await sendMessage([{
        id: uploaded.id,
        name: uploaded.name,
        mime: uploaded.mime,
        path: uploaded.path,
        size: uploaded.size,
        type: 'image',
      }])
    } finally {
      setUploadingAttachment(false)
      requestAnimationFrame(() => composerInputRef.current?.focus())
    }
  }, [selectedUserIdNum, sendMessage, sending, uploadingAttachment])

  if (!drawerOpen) return null

  return (
    <BodyPortal>
      {!pinned && (
        <div
          className="chat-drawer-backdrop"
          onClick={closeChatDrawer}
          aria-hidden
        />
      )}

      <aside
        ref={drawerRef}
        className={`chat-drawer ${pinned ? 'chat-drawer--pinned' : ''}`}
        role="dialog"
        aria-modal={pinned ? 'false' : 'true'}
        aria-label="Sohbet"
      >
        <div className="chat-drawer__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="chat-drawer__mobile-back" onClick={closeChatDrawer} aria-label="Kapat">
              <i className="fa-regular fa-arrow-left" />
            </button>
            <div>
              <div className="chat-drawer__title">Sohbet</div>
              <div className="chat-drawer__subtitle">
                {user?.admin === 1 ? 'Tüm kullanıcılar' : 'Firma içi kullanıcılar'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className={`chat-drawer__close chat-drawer__pin ${pinned ? 'chat-drawer__pin--active' : ''}`}
              aria-label={pinned ? 'Sohbeti modal yap' : 'Sohbeti sabitle'}
              title={pinned ? 'Sabitlemeyi kapat' : 'Sağa sabitle'}
              onClick={togglePinned}
            >
              <i className="fa-solid fa-thumbtack" />
            </button>
            <button
              type="button"
              className="chat-drawer__close"
              aria-label="Bildirim ayarları"
              title="Bildirim ayarları"
              onClick={() => setSettingsOpen((v) => !v)}
            >
              ⚙
            </button>
            <button className="chat-drawer__close" onClick={closeChatDrawer} type="button" aria-label="Kapat">✕</button>
          </div>
        </div>

        <div className={`chat-drawer__body ${selectedUserIdNum ? 'chat-drawer__body--chat-view' : ''}`}>
          {/* Left: contacts */}
          <div className="chat-drawer__contacts">
            <div className="chat-drawer__contacts-head">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div className="chat-drawer__contacts-title">Kişiler</div>
                  {contactsLoading && <div className="chat-drawer__contacts-loading">Yükleniyor...</div>}
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="Kişi ara..."
                    style={{
                      flex: 1,
                      height: 30,
                      padding: '0 10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 12,
                      outline: 'none'
                    }}
                  />

                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} />
                    Çevrimiçi
                  </label>
                </div>

                {settingsOpen && (
                  <div
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 10,
                      background: '#fff',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155' }}>
                      <input
                        type="checkbox"
                        checked={!!notifySettings.soundEnabled}
                        onChange={(e) => setNotifySettings({ soundEnabled: e.target.checked })}
                      />
                      Yeni mesaj sesi
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155' }}>
                      <input
                        type="checkbox"
                        checked={!!notifySettings.toastEnabled}
                        onChange={(e) => setNotifySettings({ toastEnabled: e.target.checked })}
                      />
                      Toast bildirimleri (sohbet kapalıyken)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155' }}>
                      <input
                        type="checkbox"
                        checked={!!notifySettings.toastPreviewEnabled}
                        onChange={(e) => setNotifySettings({ toastPreviewEnabled: e.target.checked })}
                        disabled={!notifySettings.toastEnabled}
                      />
                      Toast'ta mesaj içeriği görünsün
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155' }}>
                      <input
                        type="checkbox"
                        checked={!!notifySettings.toastAutoCloseEnabled}
                        onChange={(e) => setNotifySettings({ toastAutoCloseEnabled: e.target.checked })}
                        disabled={!notifySettings.toastEnabled}
                      />
                      Toast 5 saniyede kapansın
                    </label>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      Toast'a tıklayınca ilgili sohbet açılır.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="chat-drawer__contacts-list">
              {recentContacts.length > 0 && (
                <>
                  <div className="chat-drawer__contacts-section">Görüşülenler</div>
                  {recentContacts.map((c) => {
                    const active = c.id === selectedUserIdNum
                    const colors = avatarColor(c.name)
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`chat-contact ${active ? 'chat-contact--active' : ''}`}
                        onClick={() => {
                          stickToBottomRef.current = true
                          setSelectedUserId(c.id)
                          setTimeout(() => composerInputRef.current?.focus(), 350)
                        }}
                      >
                        <div className="chat-contact__avatar">
                          <span
                            style={{
                              background: colors.bg,
                              color: colors.fg,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 999
                            }}
                          >
                            {getInitials(c.name)}
                          </span>
                          <span className={`chat-contact__status ${c.online ? 'online' : ''}`} />
                        </div>

                        <div className="chat-contact__meta">
                          <div className="chat-contact__top">
                            <div className="chat-contact__name">
                              {c.name}
                              {user?.admin === 1 && c.firm_name && <span className="chat-contact__firm">{c.firm_name}</span>}
                            </div>
                            <div className="chat-contact__timecol">
                              <div className="chat-contact__time">{formatTime(c.last_message_at || c.last_seen)}</div>
                              {c.unread_count > 0 && <div className="chat-contact__unread">{c.unread_count}</div>}
                            </div>
                          </div>
                          <div className="chat-contact__bottom">
                            <div className="chat-contact__preview">
                              {c.last_message_text ? c.last_message_text : (c.online ? 'Çevrimiçi' : '—')}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </>
              )}

              {otherContacts.length > 0 && (
                <>
                  <div className="chat-drawer__contacts-section">Diğerleri</div>
                  {otherContacts.map((c) => {
                    const active = c.id === selectedUserIdNum
                    const colors = avatarColor(c.name)
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`chat-contact ${active ? 'chat-contact--active' : ''}`}
                        onClick={() => {
                          stickToBottomRef.current = true
                          setSelectedUserId(c.id)
                          setTimeout(() => composerInputRef.current?.focus(), 350)
                        }}
                      >
                        <div className="chat-contact__avatar">
                          <span
                            style={{
                              background: colors.bg,
                              color: colors.fg,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 999
                            }}
                          >
                            {getInitials(c.name)}
                          </span>
                          <span className={`chat-contact__status ${c.online ? 'online' : ''}`} />
                        </div>

                        <div className="chat-contact__meta">
                          <div className="chat-contact__top">
                            <div className="chat-contact__name">
                              {c.name}
                              {user?.admin === 1 && c.firm_name && <span className="chat-contact__firm">{c.firm_name}</span>}
                            </div>
                            <div className="chat-contact__timecol">
                              <div className="chat-contact__time">{formatTime(c.last_message_at || c.last_seen)}</div>
                              {c.unread_count > 0 && <div className="chat-contact__unread">{c.unread_count}</div>}
                            </div>
                          </div>
                          <div className="chat-contact__bottom">
                            <div className="chat-contact__preview">
                              {c.last_message_text ? c.last_message_text : (c.online ? 'Çevrimiçi' : '—')}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </>
              )}

              {!contactsLoading && contacts.length === 0 && (
                <div className="chat-drawer__empty">Kullanıcı bulunamadı</div>
              )}
            </div>
          </div>

          {/* Right: chat */}
          <div className="chat-drawer__chat">
            <div className={`chat-drawer__chat-head ${selectedContact ? 'chat-drawer__chat-head--mobile' : ''}`}>
              {selectedContact ? (
                <>
                  <div className="chat-peer">
                    <button
                      className="chat-drawer__mobile-back"
                      onClick={() => { setSelectedUserId(null); setMessages([]) }}
                      aria-label="Geri"
                    >
                      <i className="fa-regular fa-arrow-left" />
                    </button>
                    <div
                      className="chat-peer__avatar"
                      style={{ background: avatarColor(selectedContact.name).bg }}
                    >
                      {getInitials(selectedContact.name)}
                    </div>
                    <div className="chat-peer__meta">
                      <div className="chat-peer__name">{selectedContact.name}</div>
                      <div className="chat-peer__status">
                        {selectedContact.online ? 'Çevrimiçi' : 'Çevrimdışı'}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="chat-peer">Kişi seçin</div>
              )}
            </div>

            <div
              className="chat-drawer__messages"
              ref={messagesWrapRef}
              onClick={(event) => {
                if (!selectedUserIdNum) return
                const target = event.target as HTMLElement | null
                if (target?.closest('button, a, input, textarea, select')) return
                focusComposer()
              }}
              onScroll={() => {
                const el = messagesWrapRef.current
                if (!el) return
                const dist = el.scrollHeight - el.scrollTop - el.clientHeight
                stickToBottomRef.current = dist < 120
              }}
            >
              {messagesLoading && <div className="chat-drawer__messages-loading">Yükleniyor...</div>}

              {!messagesLoading && !selectedUserIdNum && (
                <div className="chat-drawer__messages-empty">Soldan bir kişi seçin</div>
              )}

              {!messagesLoading && selectedUserIdNum && messages.length === 0 && (
                <div className="chat-drawer__messages-empty">Henüz mesaj yok</div>
              )}

              {messages.map((m) => {
                const mine = m.from_user_id === user?.id
                const attachments = Array.isArray(m.attachments) ? m.attachments : []
                const hasImageAttachment = attachments.some((attachment) => {
                  return String(attachment.mime || '').startsWith('image/') || attachment.type === 'image'
                })
                return (
                  <div key={m.id} className={`chat-msg ${mine ? 'chat-msg--mine' : 'chat-msg--theirs'}`}>
                    <div className={`chat-msg__bubble ${hasImageAttachment ? 'chat-msg__bubble--media' : ''}`}>
                      {attachments.length > 0 && (
                        <div className="chat-msg__attachments">
                          {attachments.map((attachment, index) => {
                            const src = fileUrl(attachment.path)
                            const isImage = String(attachment.mime || '').startsWith('image/') || attachment.type === 'image'
                            if (isImage) {
                              return (
                                <button
                                  key={`${attachment.path}-${index}`}
                                  type="button"
                                  className="chat-msg__image-link"
                                  title={attachment.name}
                                  onClick={() => setPreviewImage({ src, name: attachment.name || 'Görsel' })}
                                >
                                  <img src={src} alt={attachment.name || 'Görsel'} className="chat-msg__image" />
                                </button>
                              )
                            }
                            return (
                              <a
                                key={`${attachment.path}-${index}`}
                                className="chat-msg__file"
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="fa-regular fa-paperclip" />
                                <span>{attachment.name || 'Dosya'}</span>
                              </a>
                            )
                          })}
                        </div>
                      )}
                      {m.message && <div className="chat-msg__text">{m.message}</div>}
                      <div className="chat-msg__time">{formatTime(m.created_at)}</div>
                    </div>
                  </div>
                )
              })}
              <div ref={bottomRef} />
            </div>

            <div className="chat-drawer__composer">
              <input
                ref={composerInputRef}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onPaste={handleComposerPaste}
                placeholder={selectedUserIdNum ? (uploadingAttachment ? 'Görsel yükleniyor...' : 'Mesaj yaz... (Enter ile gönder)') : 'Önce kişi seçin...'}
                className="chat-drawer__input"
                onKeyDown={(e) => {
                  if ((e as any).nativeEvent?.isComposing) return
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (sending || uploadingAttachment) return
                    sendMessage()
                  }
                }}
                disabled={!selectedUserIdNum || uploadingAttachment}
              />
            </div>
          </div>
        </div>
      </aside>

      {previewImage && (
        <div className="chat-image-modal" role="dialog" aria-modal="true" onClick={() => setPreviewImage(null)}>
          <div className="chat-image-modal__frame" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="chat-image-modal__close"
              onClick={() => setPreviewImage(null)}
              aria-label="Kapat"
            >
              ✕
            </button>
            <img src={previewImage.src} alt={previewImage.name} className="chat-image-modal__image" />
          </div>
        </div>
      )}
    </BodyPortal>
  )
}
